import { useEffect, useRef, useState } from "react";
import { addDataLocListeners } from "#scripts/lib/dev/addDataLocListeners.tsx";
import { FileViewer, type FileViewerFile } from "./file-viewer.tsx";
import { type ConsoleLine, MockConsole } from "./mock-console.tsx";

interface ClickToSourceDemoProps {
  children: React.ReactNode;
  fileMap: Record<string, string>;
}

export function ClickToSourceDemo({ children, fileMap }: ClickToSourceDemoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [consoleLines, setConsoleLines] = useState<ConsoleLine[]>([]);
  const [currentFile, setCurrentFile] = useState<FileViewerFile | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const cleanup = addDataLocListeners(container, ({ file, line, column }) => {
      const timestamp = new Date();
      const lineId = `${timestamp.getTime()}`;

      setConsoleLines((prev) => [
        ...prev,
        {
          id: lineId,
          message: `Opening ${file}:${line}:${column}`,
          timestamp,
        },
      ]);

      const content = fileMap[file];
      if (content) {
        setCurrentFile({
          path: file,
          content,
          highlightLine: line,
        });
      }
    });

    return cleanup;
  }, [fileMap]);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-bg border border-border rounded-md">
        <div data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:49:14" data-name="div" className="mb-2 text-xs text-text-secondary">
          <span data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:50:17" data-name="span" className="inline-flex items-center gap-1">
            <kbd data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:51:18" data-name="kbd" className="px-1.5 py-0.5 bg-bg-elevated border border-border rounded text-[10px]">Alt</kbd>
            <span>+ Hover to inspect</span>
          </span>
          <span data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:54:17" data-name="span" className="mx-2">•</span>
          <span data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:55:17" data-name="span" className="inline-flex items-center gap-1">
            <kbd data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:56:18" data-name="kbd" className="px-1.5 py-0.5 bg-bg-elevated border border-border rounded text-[10px]">Alt</kbd>
            <span>+ Click to view source</span>
          </span>
        </div>
        <div ref={containerRef} data-loc="phosphor-blog/scripts/components/demo/click-to-source-demo.tsx:60:33" data-name="div" className="p-6 bg-bg-elevated rounded-md border border-border-subtle">
          {children}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MockConsole lines={consoleLines} />
        <FileViewer file={currentFile} />
      </div>
    </div>
  );
}
